import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {
  twoFactorCancelledError,
  twoFactorMaxAttemptsError,
} from '@gatewee/services/two-factor-auth.service';
import {GenericCrudService} from '@gatewee/services/generic-crud.service';
import {PaymentProviderResponse, PaymentReceipt} from '@gatewee/types/payment';
import {FeeSnapshot} from '@gatewee/types/fee';
import {
  BankSlipPayPayload,
  BankSlipValidateResponse,
  Withdrawal,
  WithdrawalStatus,
} from '@gatewee/types/withdrawal';
import {
  calculateWithdrawalAmounts,
  extractPaymentMetadataError,
  getWithdrawalStatusErrorMessage,
  normalizeDigitableLine,
  resolveWithdrawalAmounts,
  validateDigitableLine,
  WithdrawalAmountBreakdown,
} from '@gatewee/utils';
import {TranslateService} from '@ngx-translate/core';
import {TuiAlertService} from '@taiga-ui/core';
import {interval, Subscription} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {WithdrawalsService} from '../../withdrawals.service';

export type BankSlipShellKind = 'dialog' | 'pageDrawer';
export type BankSlipFlowStep = 'digitable' | 'review' | 'payment_proof';

@Component({
  selector: 'app-bank-slip-payment-form',
  templateUrl: './bank-slip-payment-form.component.html',
  standalone: false,
})
export class BankSlipPaymentFormComponent implements OnDestroy {
  @Input() flowActive = false;
  @Input() shellKind: BankSlipShellKind = 'pageDrawer';
  @Input() feeSnapshot: FeeSnapshot | null = null;
  @Input() walletId: number | null = null;
  @Input() walletAvailableBalance: number | null = null;

  @Output() readonly completed = new EventEmitter<void>();
  @Output() readonly scanRequested = new EventEmitter<void>();
  @Output() readonly embeddedTitleKeyChange = new EventEmitter<string>();
  @Output() readonly shellFooterChange = new EventEmitter<void>();

  @ViewChild('actionFooter', {read: TemplateRef})
  private actionFooterTplRef?: TemplateRef<unknown>;

  readonly form: FormGroup;
  step: BankSlipFlowStep = 'digitable';
  submitted = false;
  isValidating = false;
  isSubmitting = false;
  validated: BankSlipValidateResponse | null = null;
  transferErrorMessage: string | null = null;
  createdWithdrawal: Withdrawal | null = null;

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly payCrud: GenericCrudService<Withdrawal>;
  private pollingSub?: Subscription;

  private readonly i18n = 'features.withdrawals.modalBankSlip';

  constructor(
    private readonly fb: FormBuilder,
    private readonly http: HttpClient,
    private readonly withdrawalsService: WithdrawalsService,
    private readonly translate: TranslateService,
    private readonly alerts: TuiAlertService,
  ) {
    this.form = this.fb.group({
      digitableLine: ['', [Validators.required, validateDigitableLine]],
      description: [''],
    });
    this.payCrud = new GenericCrudService<Withdrawal>(
      '/withdrawals/recipient/bank-slip',
      this.http,
    );
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  get actionFooterTpl(): TemplateRef<unknown> | null {
    return this.actionFooterTplRef ?? null;
  }

  get currentTitleKey(): string {
    switch (this.step) {
      case 'review':
        return `${this.i18n}.drawerTitleReview`;
      case 'payment_proof':
        return `${this.i18n}.drawerTitleProof`;
      default:
        return `${this.i18n}.title`;
    }
  }

  get digitableDigits(): string {
    return normalizeDigitableLine(this.form.get('digitableLine')?.value);
  }

  get canContinueDigitable(): boolean {
    const ctrl = this.form.get('digitableLine');
    return !!ctrl && ctrl.valid && !this.isValidating;
  }

  get amountBreakdown(): WithdrawalAmountBreakdown | null {
    if (this.createdWithdrawal) {
      return resolveWithdrawalAmounts(this.createdWithdrawal, this.feeSnapshot);
    }
    const face = this.validated?.amount;
    if (typeof face !== 'number' || !Number.isFinite(face) || face <= 0) {
      return null;
    }
    return calculateWithdrawalAmounts(face, this.feeSnapshot, 'addFees');
  }

  get insufficientBalance(): boolean {
    const debit = this.amountBreakdown?.amount;
    if (
      debit === undefined ||
      this.walletAvailableBalance === null ||
      this.walletAvailableBalance === undefined
    ) {
      return false;
    }
    return debit > this.walletAvailableBalance;
  }

  get paymentReceipt(): PaymentReceipt | undefined {
    return this.createdWithdrawal?.paymentMetadata?.receipt ?? undefined;
  }

  get paymentMetadata(): PaymentProviderResponse<unknown, unknown> | undefined {
    return this.createdWithdrawal?.paymentMetadata as
      | PaymentProviderResponse<unknown, unknown>
      | undefined;
  }

  get isWithdrawalPolling(): boolean {
    const status = this.createdWithdrawal?.status;
    return (
      status === WithdrawalStatus.PENDING ||
      status === WithdrawalStatus.PROCESSING
    );
  }

  get isWithdrawalSuccess(): boolean {
    return this.createdWithdrawal?.status === WithdrawalStatus.SUCCESS;
  }

  get isWithdrawalFailed(): boolean {
    const status = this.createdWithdrawal?.status;
    return (
      status === WithdrawalStatus.FAILED ||
      status === WithdrawalStatus.CANCELED ||
      status === WithdrawalStatus.REJECTED
    );
  }

  get showWithdrawalFailureUi(): boolean {
    return this.isWithdrawalFailed || !!this.withdrawalStatusErrorMessage;
  }

  get withdrawalStatusErrorMessage(): string | null {
    if (this.transferErrorMessage) {
      return this.transferErrorMessage;
    }
    if (!this.createdWithdrawal || !this.isWithdrawalFailed) {
      return null;
    }
    return getWithdrawalStatusErrorMessage(
      this.createdWithdrawal,
      this.translate,
    );
  }

  get descriptionValue(): string {
    return String(this.form.get('description')?.value ?? '').trim();
  }

  /** Parent returns to hub/close when `false`. */
  handleBack(): boolean {
    if (this.step === 'review') {
      this.goToDigitable();
      return true;
    }
    if (this.step === 'payment_proof') {
      return false;
    }
    return false;
  }

  setDigitableFromScan(raw: string): void {
    const digits = normalizeDigitableLine(raw);
    const ctrl = this.form.get('digitableLine');
    if (!ctrl) {
      return;
    }
    ctrl.setValue(digits);
    ctrl.markAsTouched();
    ctrl.updateValueAndValidity();
    this.submitted = false;
    this.cdr.markForCheck();
  }

  onContinueFromDigitable(): void {
    this.submitted = true;
    const ctrl = this.form.get('digitableLine');
    if (!ctrl?.valid) {
      ctrl?.markAsTouched();
      return;
    }
    const digitableLine = this.digitableDigits;
    ctrl.setValue(digitableLine, {emitEvent: false});
    this.isValidating = true;
    this.transferErrorMessage = null;
    this.shellFooterChange.emit();

    this.withdrawalsService.validateBankSlip(digitableLine, true).subscribe({
      next: response => {
        this.isValidating = false;
        this.validated = {
          ...response,
          digitableLine: normalizeDigitableLine(
            response.digitableLine || digitableLine,
          ),
        };
        this.setStep('review');
      },
      error: (err: HttpErrorResponse) => {
        this.isValidating = false;
        this.shellFooterChange.emit();
        this.alerts
          .open(this.resolveHttpErrorMessage(err), {
            label: this.translate.instant(`${this.i18n}.validateErrorTitle`),
            appearance: 'error',
            autoClose: 5000,
          })
          .subscribe();
      },
    });
  }

  onSubmit(): void {
    if (this.isSubmitting || !this.validated || this.insufficientBalance) {
      return;
    }
    if (this.walletId === null || this.walletId === undefined) {
      this.transferErrorMessage = this.translate.instant(
        `${this.i18n}.walletRequired`,
      );
      return;
    }

    this.isSubmitting = true;
    this.transferErrorMessage = null;
    this.shellFooterChange.emit();

    const payload: BankSlipPayPayload = {
      digitableLine: this.validated.digitableLine,
      walletId: this.walletId,
      description:
        this.descriptionValue ||
        this.translate.instant(`${this.i18n}.defaultDescription`),
    };

    this.payCrud
      .createWith2Fa(payload as unknown as Withdrawal, true)
      .subscribe({
        next: (withdrawal: Withdrawal) => {
          this.isSubmitting = false;
          this.createdWithdrawal = withdrawal;
          this.setStep('payment_proof');
          if (
            withdrawal?.id &&
            (withdrawal.status === WithdrawalStatus.PENDING ||
              withdrawal.status === WithdrawalStatus.PROCESSING)
          ) {
            this.startPolling(withdrawal.id);
          }
        },
        error: (err: HttpErrorResponse | Error) => {
          this.isSubmitting = false;
          this.shellFooterChange.emit();
          if (err instanceof Error && err.message === twoFactorCancelledError) {
            return;
          }
          if (
            err instanceof Error &&
            err.message === twoFactorMaxAttemptsError
          ) {
            this.alerts
              .open(
                this.translate.instant('common.twoFactor.maxAttemptsExceeded'),
                {
                  label: this.translate.instant('common.twoFactor.title'),
                  appearance: 'error',
                  autoClose: 5000,
                },
              )
              .subscribe();
            return;
          }
          this.transferErrorMessage = this.resolveSubmitErrorMessage(err);
          this.cdr.markForCheck();
        },
      });
  }

  onProofConclude(): void {
    this.reset();
    this.completed.emit();
  }

  reset(): void {
    this.stopPolling();
    this.step = 'digitable';
    this.submitted = false;
    this.isValidating = false;
    this.isSubmitting = false;
    this.validated = null;
    this.transferErrorMessage = null;
    this.createdWithdrawal = null;
    this.form.reset({digitableLine: '', description: ''});
    this.emitTitle();
    this.shellFooterChange.emit();
  }

  private goToDigitable(): void {
    this.validated = null;
    this.transferErrorMessage = null;
    this.setStep('digitable');
  }

  private setStep(next: BankSlipFlowStep): void {
    this.step = next;
    this.emitTitle();
    this.shellFooterChange.emit();
    this.cdr.markForCheck();
  }

  private emitTitle(): void {
    this.embeddedTitleKeyChange.emit(this.currentTitleKey);
  }

  private startPolling(id: number): void {
    this.pollingSub?.unsubscribe();
    this.pollingSub = interval(5000)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.withdrawalsService.findOne(id, undefined, true)),
      )
      .subscribe({
        next: (w: Withdrawal) => {
          this.createdWithdrawal = w;
          if (
            w.status !== WithdrawalStatus.PENDING &&
            w.status !== WithdrawalStatus.PROCESSING
          ) {
            this.stopPolling();
          }
          this.cdr.markForCheck();
        },
      });
  }

  private stopPolling(): void {
    this.pollingSub?.unsubscribe();
    this.pollingSub = undefined;
  }

  private resolveHttpErrorMessage(err: HttpErrorResponse): string {
    const fallback = this.translate.instant(`${this.i18n}.validateError`);
    const body = err.error;
    if (typeof body === 'string' && body.trim()) {
      return body;
    }
    if (body && typeof body === 'object') {
      const message = (body as {message?: string}).message;
      if (message?.trim()) {
        return message;
      }
    }
    return fallback;
  }

  private resolveSubmitErrorMessage(err: HttpErrorResponse | Error): string {
    const fallback = this.translate.instant(`${this.i18n}.payError`);
    if (!(err instanceof HttpErrorResponse)) {
      return fallback;
    }
    const body = err.error;
    if (typeof body === 'string' && body.trim()) {
      return body;
    }
    if (body && typeof body === 'object') {
      const providerError = extractPaymentMetadataError(
        (body as Withdrawal).paymentMetadata ??
          (body as {paymentMetadata?: Withdrawal['paymentMetadata']})
            .paymentMetadata,
      );
      if (providerError) {
        return providerError;
      }
      const message = (body as {message?: string}).message;
      if (message?.trim()) {
        return message;
      }
    }
    return fallback;
  }
}
